import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
    getEquipmentsError,
    getEquipmentsWithMatSer,
    getFormWithDocId,
    navigateToForms as navigateToFormWithDocId,
} from '../actions/authors.action';
import { catchError, map, of, switchMap } from 'rxjs';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { EquipmentService } from '../../services/equipment/equipment.service';
import { NotificationService } from '../../services/notification/notification.service';
import { FormsConstants, FormUtilities, loadSpqDoc, navigateToForm } from '@evorait/pace-forms';
import { isDefined } from '@evorait/pace-core';

@Injectable({
    providedIn: 'root',
})
export class AuthorsEffectsService {
    public constructor(
        private actions$: Actions,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly equipmentService: EquipmentService,
        private readonly notificationService: NotificationService,
    ) {}

    public getEquipmentsWithDocId$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getFormWithDocId),
            map((action) => {
                this.notificationService.createDummyNotificationForAuthors();
                return navigateToFormWithDocId({ docId: action.docId });
            }),
        ),
    );

    public getEquipmentsWithMatSer$ = createEffect(() =>
        this.actions$.pipe(
            ofType(getEquipmentsWithMatSer),
            switchMap((action) => {
                return this.equipmentService.getEquipmentsByMatSer(action.materialNumber, action.serialNumber).pipe(
                    map((response) => {
                        const data = response[0];
                        this.notificationService.createDummyNotificationForAuthors(data);

                        return navigateToFormWithDocId({ docId: action.docId });
                    }),
                    catchError((error) => {
                        this.log.error(
                            `AuthorsEffects: getEquipmentsWithMatSer$: error in get equipments with material/serial number: ${JSON.stringify(
                                error,
                                Object.getOwnPropertyNames(error),
                            )}`,
                        );
                        return of(getEquipmentsError({ error }));
                    }),
                );
            }),
        ),
    );

    public navigateToFormsWithDocId$ = createEffect(() =>
        this.actions$.pipe(
            ofType(navigateToFormWithDocId),
            map((action) => {
                const notification = this.notificationService.getNotificationStateByIdentifier();
                if (!isDefined(notification)) {
                    throw new Error(`AuthorsEffectsService: navigateToFormsWithDocId$: missing notification`);
                }

                const mainEquipment = this.notificationService.getMainEquipmentFromNotification(notification);
                if (!isDefined(mainEquipment)) {
                    throw new Error(`AuthorsEffectsService: navigateToFormsWithDocId$: missing main equipment`);
                }
                const formType = FormUtilities.getFormTypeFromId(action.docId);
                if (FormUtilities.isServicePartQuestionnaire(formType)) {
                    return loadSpqDoc({
                        form: {
                            equipmentId: mainEquipment.equnr,
                            formId: action.docId,
                            materialId: '',
                        },
                        notification: notification.identifier,
                        materialKey: '',
                        language: notification.languageId,
                        materialNumber: '',
                    });
                } else {
                    return navigateToForm({
                        notification: notification.identifier,
                        form: {
                            equipmentId: mainEquipment.equnr,
                            formId: action.docId,
                            materialId: mainEquipment.matnr,
                        },
                        status: FormsConstants.FORMSTATUS.LOCK,
                    });
                }
            }),
        ),
    );
}
